import React, { useState } from 'react';
import { uploadUrl } from '../../../functions/Api';
import { Button } from '@asu/unity-react-core';
import errorLogger from '../../../utils/ErrorLogger';

const UploadByUrl = (props) => {
  const [url, setUrl] = useState('https://');
  const [isValidating, setIsValidating] = useState(false);

  const uploadFiles = async () => {
    props.setUploading(true);

    for (const selectedClass of props.classes) {
      try {
        let result = await uploadUrl(url, selectedClass);
        props.setUploadResults((results) => results.concat(result));
      } catch (error) {
        errorLogger.logError(error, 'upload-by-url', {
          url: errorLogger.sanitizeUrl(url),
          classNumber: selectedClass.classNumber
        });

        props.setErrors((e) => {
          if (e.includes(error)) {
            return e;
          } else {
            return e.concat(error);
          }
        });
      }
    }

    props.updateSyllabusStatus();
    props.setUploading(false);
  };

  const changeHandler = (e) => {
    setUrl(e.target.value);
  };

  const validateUrl = (urlToValidate) => {
    try {
      // Auto-fix common issues
      let fixedUrl = urlToValidate.trim();
      
      // If URL doesn't start with http:// or https://, add https://
      if (!fixedUrl.match(/^https?:\/\//i)) {
        fixedUrl = 'https://' + fixedUrl;
      }

      // Use URL constructor for validation
      const urlObject = new URL(fixedUrl);
      
      // Additional validation - must be http or https
      if (!['http:', 'https:'].includes(urlObject.protocol)) {
        throw new Error('URL must use HTTP or HTTPS protocol');
      }

      // Must have a valid hostname
      if (!urlObject.hostname || urlObject.hostname.length === 0) {
        throw new Error('URL must have a valid hostname');
      }

      return { isValid: true, fixedUrl, error: null };
    } catch (error) {
      return { 
        isValid: false, 
        fixedUrl: urlToValidate, 
        error: error.message 
      };
    }
  };

  const handleSave = () => {
    try {
      setIsValidating(true);
      
      const validation = validateUrl(url);
      
      if (validation.isValid) {
        // Update the URL if it was auto-fixed
        if (validation.fixedUrl !== url) {
          setUrl(validation.fixedUrl);
        }
        
        // Use the fixed URL for upload
        const urlToUpload = validation.fixedUrl;
        
        // Create upload function with validated URL
        const uploadWithValidatedUrl = async () => {
          props.setUploading(true);

          for (const selectedClass of props.classes) {
            try {
              let result = await uploadUrl(urlToUpload, selectedClass);
              props.setUploadResults((results) => results.concat(result));
            } catch (error) {
              errorLogger.logError(error, 'upload-by-url', {
                url: errorLogger.sanitizeUrl(urlToUpload),
                classNumber: selectedClass.classNumber
              });

              props.setErrors((e) => {
                if (e.includes(error)) {
                  return e;
                } else {
                  return e.concat(error);
                }
              });
            }
          }

          props.updateSyllabusStatus();
          props.setUploading(false);
        };
        
        uploadWithValidatedUrl();
      } else {
        // Show user-friendly error message
        const errorMessage = `Please enter a valid URL. ${validation.error}`;
        alert(errorMessage);
        
        // Add to errors array for consistent error handling
        props.setErrors((e) => {
          const urlError = new Error(errorMessage);
          urlError.type = 'validation';
          return e.concat(urlError);
        });
      }
    } catch (error) {
      // Catch any unexpected errors to prevent blank page
      errorLogger.logError(error, 'upload-url-validation', {
        url: errorLogger.sanitizeUrl(url),
        context: 'handleSave'
      });
      
      alert('An error occurred while validating the URL. Please try again.');
      
      props.setErrors((e) => {
        const validationError = new Error('URL validation failed: ' + error.message);
        validationError.type = 'validation';
        return e.concat(validationError);
      });
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="action-buttons">
      <p>
        Please verify that the URL is a publicly-reachable URL, eg.,{' '}
        <a href="https://www.asu.edu">https://www.asu.edu</a>
      </p>
      <div className="form-row mb-2">
        <div className="form-group">
          <input
            className="form-control"
            id="url"
            type="text"
            name="url"
            value={url}
            onChange={changeHandler}
            placeholder="https://example.com/document.pdf"
            disabled={isValidating}
          />
          <small className="form-text text-muted">
            URLs will be automatically corrected if missing https://
          </small>
        </div>
      </div>
      <Button 
        label={isValidating ? "Validating..." : "Save"} 
        color="maroon" 
        onClick={handleSave}
        disabled={isValidating || !url || url.trim() === '' || url.trim() === 'https://'}
      />
    </div>
  );
};

export default UploadByUrl;
